// Discovery-mode feature extraction. Owns the discoveryFeatures payload,
// the manual refresh + trigger callbacks, and the 25s post-turn auto-
// refresh effect that catches the Service Bus worker's output once the
// flow has settled. The orchestrator's load effect and import flows
// also update the payload via setDiscoveryFeatures (exposed below) —
// keeping them out of this hook avoids duplicating session-load logic.

import {
  useCallback,
  useEffect,
  useState,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from 'react';
import type { ConversationMode, DiscoveryFeaturesPayload } from './sessionTypes';

export interface UseDiscoveryFeaturesOptions {
  sessionIdRef: MutableRefObject<string | null>;
  modeRef: MutableRefObject<ConversationMode>;
  // Latest flow text from the orchestrator. The auto-refresh effect
  // re-fetches features whenever the statement count changes.
  flowText: string;
}

export interface UseDiscoveryFeaturesReturn {
  discoveryFeatures: DiscoveryFeaturesPayload;
  setDiscoveryFeatures: Dispatch<SetStateAction<DiscoveryFeaturesPayload>>;
  isExtractingFeatures: boolean;
  refreshDiscoveryFeatures: () => Promise<void>;
  triggerDiscoveryFeatureExtraction: () => Promise<void>;
}

const EMPTY_PAYLOAD: DiscoveryFeaturesPayload = {
  features: [],
  extractedAt: null,
  statementCountAtExtraction: null,
  warnings: [],
  status: 'idle',
};

export function useDiscoveryFeatures(
  opts: UseDiscoveryFeaturesOptions,
): UseDiscoveryFeaturesReturn {
  const { sessionIdRef, modeRef, flowText } = opts;

  const [discoveryFeatures, setDiscoveryFeatures] =
    useState<DiscoveryFeaturesPayload>(EMPTY_PAYLOAD);
  const [isExtractingFeatures, setIsExtractingFeatures] = useState(false);

  // ── Manual refresh ────────────────────────────────────────────
  // The Service Bus worker debounces feature extraction by ~20s after
  // each chat2 turn. This callback lets the panel re-pull whenever the
  // user asks (or after the auto-refresh effect below fires).
  const refreshDiscoveryFeatures = useCallback(async () => {
    const id = sessionIdRef.current;
    if (!id) return;
    try {
      const res = await fetch(`/api/sessions/${id}/discovery-features`);
      if (!res.ok) return;
      const data = await res.json();
      setDiscoveryFeatures({
        features: data.features || [],
        extractedAt: data.extractedAt ?? null,
        statementCountAtExtraction: data.statementCountAtExtraction ?? null,
        warnings: data.warnings || [],
        status: data.status || 'idle',
      });
    } catch (err) {
      console.error('[Features] refresh failed:', err);
    }
  }, [sessionIdRef]);

  // ── Manual trigger (bypasses queue debounce) ──────────────────
  const triggerDiscoveryFeatureExtraction = useCallback(async () => {
    const id = sessionIdRef.current;
    if (!id) return;
    setIsExtractingFeatures(true);
    setDiscoveryFeatures((prev) => ({ ...prev, status: 'pending' }));
    try {
      const res = await fetch(`/api/sessions/${id}/discovery-features`, { method: 'POST' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setDiscoveryFeatures({
        features: data.features || [],
        extractedAt: data.extractedAt ?? null,
        statementCountAtExtraction: data.statementCountAtExtraction ?? null,
        warnings: data.warnings || [],
        status: 'idle',
      });
    } catch (err) {
      console.error('[Features] manual extraction failed:', err);
      setDiscoveryFeatures((prev) => ({ ...prev, status: 'error' }));
    } finally {
      setIsExtractingFeatures(false);
    }
  }, [sessionIdRef]);

  // ── Auto-refresh after a flow change ──────────────────────────
  // After a chat2 turn that changed the flow, the server enqueues a
  // 20s-debounced extraction job. Refresh once around when it should
  // be done; if the flow is still settling we'll catch the next turn.
  useEffect(() => {
    if (modeRef.current !== 'discovery') return;
    if (!sessionIdRef.current) return;
    if (!flowText.trim()) return;
    const stmtCount = (flowText.match(/^\s*\[\d+\]/gm) || []).length;
    if (stmtCount === 0) return;
    if (stmtCount === discoveryFeatures.statementCountAtExtraction) return;
    const timer = setTimeout(() => { refreshDiscoveryFeatures(); }, 25000);
    return () => clearTimeout(timer);
  }, [
    flowText,
    discoveryFeatures.statementCountAtExtraction,
    refreshDiscoveryFeatures,
    modeRef,
    sessionIdRef,
  ]);

  return {
    discoveryFeatures,
    setDiscoveryFeatures,
    isExtractingFeatures,
    refreshDiscoveryFeatures,
    triggerDiscoveryFeatureExtraction,
  };
}
